import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/tailwind.utils'
import { ArrowRight } from 'lucide-react'
import * as React from 'react'
import { useCallback } from 'react'
import { guideBadgeClassMap } from '../config/guide.config'

export type GuideCardProps = React.HTMLAttributes<HTMLDivElement> & {
  title: string
  description: string
  type?: keyof typeof guideBadgeClassMap
  actionLabel?: string
  onActionClick?: () => void
  disabled?: boolean
  icon?: React.ReactNode
}

export const GuideCard = React.memo(function GuideCard({
  title,
  description,
  type = 'tutorials',
  actionLabel = 'View',
  onActionClick,
  icon,
  className,
  disabled = false,
  ...props
}: GuideCardProps) {
  const handleCardClick = useCallback(() => {
    if (disabled) return
    onActionClick?.()
  }, [disabled, onActionClick])

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (disabled) return
      onActionClick?.()
    },
    [disabled, onActionClick],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onActionClick?.()
      }
    },
    [disabled, onActionClick],
  )

  return (
    <Card
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={`${type}: ${title}. ${actionLabel}`}
      className={cn(
        'cursor-pointer overflow-hidden rounded-lg border border-border bg-card shadow-sm',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 rounded-lg px-6 pb-0 pt-6">
        {type && (
          <Badge size="xs" className={cn('uppercase', guideBadgeClassMap[type])}>
            {type}
          </Badge>
        )}
        <div className="flex h-14 w-14 items-center justify-center p-2 text-neutral-400">
          {icon ?? <div aria-hidden="true" />}
        </div>
      </CardHeader>

      <CardContent className="px-6 pb-0 pt-0">
        <div className="space-y-0.5">
          <CardTitle className="line-clamp-1 truncate text-base font-semibold leading-7 text-rexBlue">
            {title}
          </CardTitle>
          <p className="line-clamp-3 min-h-[4.5rem] text-sm font-normal leading-6 text-foreground">
            {description}
          </p>
        </div>
      </CardContent>

      <CardFooter className="flex justify-end rounded-lg px-5 pb-3.5 pt-1">
        <Button
          type="button"
          size="default"
          variant="ghost"
          disabled={disabled}
          onClick={handleButtonClick}
          aria-label={`${actionLabel} ${title}`}
        >
          {actionLabel}
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Button>
      </CardFooter>
    </Card>
  )
})

GuideCard.displayName = 'GuideCard'
